import { FeberApiClient } from "../../services/commonServices"
import { ILowerFirstCaseMetadata } from "../AccessMediator/types"
import { IGrantAccessDocument, IGrantAccessRequest, IRetrieveDocsReq } from "./GrantAccess/types"
import { IAIMHistory, IValidation } from "./Managements/Department/types"
import { IDivision } from "./Managements/Division/types"
import { IMoveReportsDto } from "./Managements/Move/types"
import { INotificationItem } from "./NotificationSetting/NotificationSetting"

export interface IMappingDepartment {
    objectId: string | null;
    department: string;
    status: string;
    modified: string;
    modifiedBy: string;
    created: string;
    history: string;
}

export const addDivision = async (division: IDivision) => {
    const response = (await FeberApiClient.post("Division", { ...division })).data
    return !!response && !!response.success && response.data
}

export const deleteDivision = async (divId: number) => {
    const response = (await FeberApiClient.delete("Division", {
        params: {
            id: divId
        }
    })).data
    return !!response && !!response.success && response.data
}

export const exportDocumentsInDiv = async (divTitle: string) => {
    try {
        const response = await FeberApiClient.get("Division/ExportDocumentsInDiv", {
            params: {
                divTitle: divTitle
            },
            responseType: 'blob', // Response is already a Blob
        });

        const url = window.URL.createObjectURL(response.data); // Directly use response.data as it's already a Blob
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `All_Documents_In_${divTitle.toUpperCase()}_Division.xlsx`);

        // Append link to body, simulate a click, then remove it
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url); // Clean up the object URL
        return true; // Indicate success
    } catch (error) {
        console.error("Error downloading file:", error);
        return false; // Indicate failure
    }
};

export const exportDivAdmins = async () => {
    try {
        const response = await FeberApiClient.get("Division/ExportDivCoordinators", {
            responseType: 'blob', // Response is already a Blob
        });

        const url = window.URL.createObjectURL(response.data); // Directly use response.data as it's already a Blob
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', "Division_Coordinator_Information_List.xlsx");

        // Append link to body, simulate a click, then remove it
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url); // Clean up the object URL
        return true; // Indicate success
    } catch (error) {
        console.error("Error downloading file:", error);
        return false; // Indicate failure
    }
}

export const validateDepartments = async (divTitle: string, isAIMRequest: boolean = false) => {
    const response = (await FeberApiClient.get("Division/ValidateDepartments", {
        params: {
            division: divTitle,
            isAIMRequest: isAIMRequest
        }
    })).data

    return !!response.success && response.data as IValidation[];
}

export const getAllDeptsForGrantAccess = async (division: string, uploadType: string) => {
    const response = await FeberApiClient.get("Division/GetAllDeptsForGrantAccess", {
        params: {
            division: division,
            uploadType: uploadType
        }
    })
    return !!response.data.success && response.data.data as string[];
}

export const getAllDepsFor = async (division: string, uploadType: string) => {
    const response = await FeberApiClient.get("Division/GetAllDeps", {
        params: {
            division: division,
            uploadType: uploadType
        }
    })
    return !!response.data.success && response.data.data as string[];
}

export const getDocsForGrantAccess = async (requestPayload: IRetrieveDocsReq) => {
    const response = await FeberApiClient.post("Report/GetDocsForGrantAccess", { ...requestPayload })
    return !!response.data.success && response.data.data as IGrantAccessDocument[];
}

export const sendAIMReport = async (departments: string[]) => {
    const response = (await FeberApiClient.post("Division/SendAIMReport", { departments: departments })).data
    return !!response && !!response.success
}

export const retrieveAIMHistory = async (department: string) => {
    const response = await FeberApiClient.get("Division/RetrieveAIMHistory", {
        params: {
            department: department
        }
    })
    return !!response.data.success && response.data.data as IAIMHistory[];
}

export const grantAccess = async (request: IGrantAccessRequest) => {
    const response = (await FeberApiClient.post("Report/GrantAccess", { ...request })).data
    return !!response && !!response.success
}

export const getDocsForMove = async (uploadType: string, division: string, department: string) => {
    const response = (await FeberApiClient.get("Report/GetDocsForMove", {
        params: {
            uploadType: uploadType,
            division: division,
            department: department
        }
    })).data
    return !!response && !!response.success && response.data as ILowerFirstCaseMetadata[]
}

export const moveReports = async (request: IMoveReportsDto) => {
    const response = (await FeberApiClient.post("Report/MoveReports", { ...request })).data
    return !!response && !!response.success && response.data as ILowerFirstCaseMetadata[]
}

export const anyDepNeedUpdate = async () => {
    const response = (await FeberApiClient.get("Division/AnyDepNeedUpdate")).data
    return !!response && !!response.success && !!response.data
}

export const getMappingDep = async (status?: string) => {
    const response = (await FeberApiClient.get("Division/GetMappingDeps", {
        params: {
            status: status
        }
    })).data
    return !!response && !!response.success && !!response.data && response.data as IMappingDepartment[]
}

export const updateMappingDep = async (oldDep: string, newDep: string) => {
    const response = (await FeberApiClient.get("Division/UpdateMappingDep", {
        params: {
            oldDep: oldDep,
            newDep: newDep
        }
    })).data
    return !!response && !!response.success
}

export const addNotificationItem = async (notificationItem: INotificationItem) => {
    const response = (await FeberApiClient.post("StorageTable/AddNotification", { ...notificationItem, links: JSON.stringify(notificationItem.links) })).data
    return !!response && !!response.success
}

export const getAllNotificationItems = async () => {
    const response = (await FeberApiClient.get("StorageTable/GetNotifications")).data
    return !!response && !!response.success && response.data
}

export const toggleNotificationAsync = async (rowKey: string, isActive: boolean) => {
    const response = (await FeberApiClient.get("StorageTable/ToggleNotification", {
        params: {
            rowKey: rowKey,
            isActive: isActive
        }
    })).data
    return !!response && !!response.success
}

export const getActiveNotification = async () => {
    const response = (await FeberApiClient.get("StorageTable/GetActiveNotification")).data
    return !!response && !!response.success && response.data
}

export const deleteNotification = async (rowKey: string) => {
    const response = (await FeberApiClient.delete("StorageTable/DeleteNotification", {
        params: {
            rowKey: rowKey
        }
    })).data
    return !!response && !!response.success
}

export const updateNotification = async (notificationItem: INotificationItem) => {
    const response = (await FeberApiClient.put("StorageTable/UpdateNotification", { ...notificationItem, links: JSON.stringify(notificationItem.links) })).data
    return !!response && !!response.success
}